import React, { useState, useEffect } from 'react';

interface InactivityWarningProps {
  isVisible: boolean;
  onContinue: () => void;
  onSignOut: () => void;
  warningTimeSeconds?: number;
}

/**
 * Warning modal shown before inactivity timeout
 */
export const InactivityWarning: React.FC<InactivityWarningProps> = ({
  isVisible,
  onContinue,
  onSignOut,
  warningTimeSeconds = 300 // 5 minutes default
}) => {
  const [timeLeft, setTimeLeft] = useState(warningTimeSeconds);

  useEffect(() => {
    if (!isVisible) {
      setTimeLeft(warningTimeSeconds);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, warningTimeSeconds]);

  if (!isVisible) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 animate-pulse">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Inactivity Warning
            </h3>
            <p className="text-sm text-gray-600">
              Your session is about to expire
            </p>
          </div>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-700 mb-3">
            You've been inactive for a while. For security reasons, you'll be automatically signed out in:
          </p>
          <div className="text-center bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
            <div className="text-4xl font-bold text-yellow-600 tabular-nums">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              minutes remaining
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onContinue}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Continue Session
          </button>
          <button
            onClick={onSignOut}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Sign Out
          </button>
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-4">
          Click "Continue Session" or perform any action to stay logged in
        </p>
      </div>
    </div>
  );
};
